import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    PiArrowLeftBold,
    PiArrowsClockwiseBold,
    PiCaretDownBold,
    PiFloppyDiskBold,
    PiImageBold,
    PiMagnifyingGlassBold,
    PiPlusBold,
    PiTrashBold,
    PiXBold,
} from 'react-icons/pi';
import FileDropzone from '@v2/components/ui/FileDropzone';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';
import toast from 'react-hot-toast';

/* ─── Types ──────────────────────────────────────────────────── */
type ClientForm = {
    name: string;
    legal_name: string;
    cnpj: string;
    cnae: string;
    email: string;
    phone: string;
    address: string;
    neighborhood: string;
    zip_code: string;
    city: string;
    state: string;
    notes: string;
    active: boolean;
    parent_id: string;
};

/* ─── Masks ─────────────────────────────────────────────────── */
function maskCnpjCpf(v: string) {
    const d = v.replace(/\D/g, '').slice(0, 14);
    if (d.length <= 11)
        return d.replace(/(\d{3})(\d{3})(\d{3})(\d{0,2})/, (_, a, b, c, e) =>
            [a, b, c].filter(Boolean).join('.') + (e ? '-' + e : ''));
    return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{0,2})/, (_, a, b, c, e, f) =>
        `${a}.${b}.${c}/${e}` + (f ? '-' + f : ''));
}

function maskCnae(v: string) {
    const d = v.replace(/\D/g, '').slice(0, 7);
    return d
        .replace(/^(\d{2})(\d)/, '$1.$2')
        .replace(/^(\d{2}\.\d{2})(\d)/, '$1$2')
        .replace(/^(\d{2}\.\d{2}\d)(\d)/, '$1-$2')
        .replace(/^(\d{2}\.\d{2}\d-\d)(\d)/, '$1-$2');
}

function maskPhone(v: string) {
    const d = v.replace(/\D/g, '').slice(0, 11);
    if (d.length <= 10)
        return d.replace(/(\d{2})(\d{4})(\d{0,4})/, (_, a, b, c) => `(${a}) ${b}` + (c ? `-${c}` : ''));
    return d.replace(/(\d{2})(\d{5})(\d{0,4})/, (_, a, b, c) => `(${a}) ${b}` + (c ? `-${c}` : ''));
}

function maskCep(v: string) {
    const d = v.replace(/\D/g, '').slice(0, 8);
    return d.replace(/(\d{5})(\d{0,3})/, (_, a, b) => a + (b ? '-' + b : ''));
}

/* ─── UI helpers ─────────────────────────────────────────────── */
function Field({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
    return (
        <div className={cn('flex flex-col gap-1', className)}>
            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-zinc-600">
                {label}
            </label>
            {children}
        </div>
    );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
    return (
        <p className="mb-3 text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-zinc-600">
            {children}
        </p>
    );
}

const inputCls =
    'w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-amber-400 focus:ring-1 focus:ring-amber-400/30 dark:border-zinc-700 dark:bg-zinc-800/60 dark:text-zinc-200 dark:focus:border-amber-500/60 dark:focus:ring-amber-500/20';

const EMPTY: ClientForm = {
    name: '', legal_name: '', cnpj: '', cnae: '',
    email: '', phone: '', address: '', neighborhood: '',
    zip_code: '', city: '', state: '', notes: '',
    active: true, parent_id: '',
};

/* ─── Logo Uploader ─────────────────────────────────────────── */
function LogoUploader({ clientId, currentUrl, pendingFile, onFileChange }: {
    clientId: string | undefined;
    currentUrl: string | null;
    pendingFile: File | null;
    onFileChange: (file: File | null) => void;
}) {
    const [preview, setPreview] = useState<string | null>(currentUrl);
    const [uploading, setUploading] = useState(false);

    useEffect(() => { setPreview(currentUrl); }, [currentUrl]);

    async function handleFile(file: File) {
        if (!file.type.startsWith('image/') || file.size > 2 * 1024 * 1024) {
            toast.error('Use uma imagem PNG, JPG, WEBP ou SVG de até 2 MB.');
            return;
        }
        const objectUrl = URL.createObjectURL(file);
        setPreview(objectUrl);

        if (clientId) {
            setUploading(true);
            const form = new FormData();
            form.append('logo', file);
            try {
                const { data } = await api.post(`/clients/${clientId}/logo`, form, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                setPreview(data.logo_url ?? objectUrl);
                onFileChange(null);
            } catch {
                setPreview(currentUrl);
                onFileChange(null);
            } finally {
                setUploading(false);
            }
        } else {
            onFileChange(file);
        }
    }

    async function removeLogo() {
        if (clientId) {
            await api.delete(`/clients/${clientId}/logo`).catch(() => {});
        }
        setPreview(null);
        onFileChange(null);
    }

    return (
        <div className="flex items-start gap-4">
            <div className="relative flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50 dark:border-zinc-700 dark:bg-zinc-800">
                {preview ? (
                    <img src={preview} alt="Logo" className="size-full object-contain p-1" />
                ) : (
                    <PiImageBold className="size-8 text-gray-300 dark:text-zinc-700" />
                )}
                {uploading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/70 dark:bg-zinc-900/70">
                        <PiArrowsClockwiseBold className="size-5 animate-spin text-amber-500" />
                    </div>
                )}
            </div>
            <div className="min-w-0 flex-1 space-y-2">
                <FileDropzone
                    accept="image/png,image/jpeg,image/svg+xml,image/webp"
                    maxSizeMb={2}
                    loading={uploading}
                    disabled={uploading}
                    label={preview ? 'Trocar logo' : 'Arraste o logo aqui'}
                    hint="PNG, JPG, SVG ou WebP · clique para selecionar"
                    showSelected={false}
                    className="[&>label]:py-4"
                    onFiles={(files) => {
                        if (files[0]) void handleFile(files[0]);
                    }}
                    onError={(msg) => toast.error(msg)}
                />
                {(preview || pendingFile) && (
                    <button
                        type="button"
                        onClick={() => void removeLogo()}
                        className="flex items-center gap-2 rounded-xl border border-red-200 px-3 py-1.5 text-xs font-bold text-red-500 transition hover:bg-red-50 dark:border-red-500/30 dark:hover:bg-red-500/10"
                    >
                        <PiTrashBold className="size-3.5" />
                        Remover
                    </button>
                )}
            </div>
        </div>
    );
}

/* ─── Page ─────────────────────────────────────────────────── */
export default function ClientFormPage() {
    const { id } = useParams<{ id: string }>();
    const isEdit = !!id;
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [form, setForm] = useState<ClientForm>(EMPTY);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [currentLogoUrl, setCurrentLogoUrl] = useState<string | null>(null);

    /* Carrega dados do cliente em modo edição */
    const { data: clientData, isLoading: loadingClient } = useQuery({
        queryKey: ['client-edit', id],
        queryFn: async () => (await api.get(`/clients/${id}`)).data.data as Record<string, unknown>,
        enabled: isEdit,
    });

    useEffect(() => {
        if (!clientData) return;
        const d = clientData;
        setForm({
            name:         String(d.name ?? ''),
            legal_name:   String(d.legal_name ?? ''),
            cnpj:         maskCnpjCpf(String(d.cnpj ?? '')),
            cnae:         maskCnae(String(d.cnae ?? '')),
            email:        String(d.email ?? ''),
            phone:        maskPhone(String(d.phone ?? '')),
            address:      String(d.address ?? ''),
            neighborhood: String(d.neighborhood ?? ''),
            zip_code:     maskCep(String(d.zip_code ?? '')),
            city:         String(d.city ?? ''),
            state:        String(d.state ?? ''),
            notes:        String(d.notes ?? ''),
            active:       Boolean(d.active ?? true),
            parent_id:    d.parent_id != null ? String(d.parent_id) : '',
        });
        setCurrentLogoUrl(d.logo_url as string | null ?? null);
    }, [clientData]);

    /* Busca de franqueadoras */
    const [parentSearch, setParentSearch] = useState('');
    const [parentOpen, setParentOpen] = useState(false);
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const parentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(parentSearch), 300);
        return () => clearTimeout(t);
    }, [parentSearch]);

    const { data: parentOptions, isFetching: parentFetching } = useQuery({
        queryKey: ['clients-parent-search', debouncedSearch],
        queryFn: async () => {
            const params: Record<string, string> = { per_page: '20' };
            if (debouncedSearch) params.search = debouncedSearch;
            const { data: body } = await api.get('/clients', { params });
            return (body.data as Array<{ id: number; name: string; legal_name: string | null }>)
                .filter((c) => String(c.id) !== id);
        },
        enabled: parentOpen,
        staleTime: 30_000,
    });

    useEffect(() => {
        if (!parentOpen) return;
        const handler = (e: MouseEvent) => {
            if (parentRef.current && !parentRef.current.contains(e.target as Node)) {
                setParentOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [parentOpen]);

    const { data: existingParent } = useQuery({
        queryKey: ['client-parent-name', form.parent_id],
        queryFn: async () => (await api.get(`/clients/${form.parent_id}`)).data.data as { id: number; name: string },
        enabled: !!form.parent_id && !parentOpen,
        staleTime: 300_000,
    });

    const selectedParentLabel = useCallback(() => {
        if (!form.parent_id) return '';
        const found = parentOptions?.find((p) => String(p.id) === form.parent_id);
        if (found) return found.name;
        if (existingParent && String(existingParent.id) === form.parent_id) return existingParent.name;
        return `#${form.parent_id}`;
    }, [form.parent_id, parentOptions, existingParent]);

    /* Mutação */
    const { mutate: save, isPending: saving } = useMutation({
        mutationFn: async () => {
            const payload: Record<string, unknown> = {
                ...form,
                cnpj:     form.cnpj.replace(/\D/g, '') || null,
                cnae:     form.cnae.replace(/\D/g, '') || null,
                phone:    form.phone.replace(/\D/g, '') || null,
                zip_code: form.zip_code.replace(/\D/g, '') || null,
                parent_id: form.parent_id ? Number(form.parent_id) : null,
            };
            if (isEdit) {
                return (await api.put(`/clients/${id}`, payload)).data;
            }
            return (await api.post('/clients', payload)).data;
        },
        onSuccess: async (body) => {
            queryClient.invalidateQueries({ queryKey: ['clients'] });
            queryClient.invalidateQueries({ queryKey: ['clients-stats'] });
            if (isEdit) {
                queryClient.invalidateQueries({ queryKey: ['clients', id] });
                queryClient.invalidateQueries({ queryKey: ['client-edit', id] });
                navigate(`/clients/${id}`);
            } else {
                const newId = body?.data?.id ?? body?.id;
                // Upload logo para cliente recém-criado
                if (newId && logoFile) {
                    const fd = new FormData();
                    fd.append('logo', logoFile);
                    await api.post(`/clients/${newId}/logo`, fd, {
                        headers: { 'Content-Type': 'multipart/form-data' },
                    }).catch(() => {});
                }
                navigate(newId ? `/clients/${newId}` : '/clients');
            }
        },
        onError: (err: unknown) => {
            const resp = (err as { response?: { data?: { errors?: Record<string, string[]> } } }).response?.data;
            if (resp?.errors) {
                const flat: Record<string, string> = {};
                for (const [k, v] of Object.entries(resp.errors)) flat[k] = v[0];
                setErrors(flat);
            }
        },
    });

    function set(key: keyof ClientForm, value: string | boolean) {
        setForm((f) => ({ ...f, [key]: value }));
        setErrors((e) => { const n = { ...e }; delete n[key]; return n; });
    }

    const clientName = isEdit ? (clientData?.name as string | undefined) : undefined;

    return (
        <div className="space-y-6 px-4 py-6">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-zinc-600">
                <Link to="/clients" className="hover:text-amber-500 dark:hover:text-amber-400">Clientes</Link>
                {isEdit && clientName && (
                    <>
                        <span>•</span>
                        <Link to={`/clients/${id}`} className="hover:text-amber-500 dark:hover:text-amber-400 truncate max-w-[200px]">
                            {clientName}
                        </Link>
                    </>
                )}
                <span>•</span>
                <span className="text-gray-600 dark:text-zinc-400">{isEdit ? 'Editar' : 'Novo cliente'}</span>
            </nav>

            {/* Header */}
            <div className="flex items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-black text-gray-900 dark:text-white">
                        {isEdit ? 'Editar cliente' : 'Novo cliente'}
                    </h1>
                    {isEdit && clientName && (
                        <p className="mt-0.5 text-sm text-gray-400 dark:text-zinc-500">{clientName}</p>
                    )}
                </div>
                <Link
                    to={isEdit ? `/clients/${id}` : '/clients'}
                    className="flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-2 text-sm font-bold text-gray-500 transition hover:bg-gray-50 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-800"
                >
                    <PiArrowLeftBold className="size-3.5" />
                    Voltar
                </Link>
            </div>

            {/* Form */}
            {isEdit && loadingClient ? (
                <div className="flex h-48 items-center justify-center rounded-2xl border border-gray-200 bg-white dark:border-zinc-800 dark:bg-zinc-900">
                    <PiArrowsClockwiseBold className="size-6 animate-spin text-gray-300 dark:text-zinc-700" />
                </div>
            ) : (
                <div className="space-y-6 rounded-2xl border border-gray-200 bg-white p-6 dark:border-zinc-800 dark:bg-zinc-900">

                    {/* Identificação */}
                    <section>
                        <SectionTitle>Identificação</SectionTitle>

                        {/* Logo */}
                        <div className="mb-4">
                            <p className="mb-2 text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-zinc-600">Logo / Foto</p>
                            <LogoUploader
                                clientId={id}
                                currentUrl={currentLogoUrl}
                                pendingFile={logoFile}
                                onFileChange={setLogoFile}
                            />
                        </div>

                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                            <Field label="Nome fantasia *" className="sm:col-span-2 lg:col-span-4">
                                <input className={inputCls} value={form.name} onChange={(e) => set('name', e.target.value)} />
                                {errors.name && <p className="text-xs text-red-500">{errors.name}</p>}
                            </Field>
                            <Field label="Razão social" className="sm:col-span-2 lg:col-span-4">
                                <input className={inputCls} value={form.legal_name} onChange={(e) => set('legal_name', e.target.value)} />
                            </Field>
                            <Field label="CNPJ / CPF">
                                <input
                                    className={cn(inputCls, 'font-mono')}
                                    value={form.cnpj}
                                    onChange={(e) => set('cnpj', maskCnpjCpf(e.target.value))}
                                    placeholder="00.000.000/0000-00"
                                    inputMode="numeric"
                                />
                            </Field>
                            <Field label="CNAE">
                                <input
                                    className={cn(inputCls, 'font-mono')}
                                    value={form.cnae}
                                    onChange={(e) => set('cnae', maskCnae(e.target.value))}
                                    placeholder="00.00-0-00"
                                    inputMode="numeric"
                                />
                            </Field>
                        </div>
                    </section>

                    <hr className="border-gray-100 dark:border-zinc-800" />

                    {/* Contato */}
                    <section>
                        <SectionTitle>Contato</SectionTitle>
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                            <Field label="E-mail" className="sm:col-span-2 lg:col-span-2">
                                <input className={inputCls} type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />
                                {errors.email && <p className="text-xs text-red-500">{errors.email}</p>}
                            </Field>
                            <Field label="Telefone">
                                <input
                                    className={cn(inputCls, 'font-mono')}
                                    value={form.phone}
                                    onChange={(e) => set('phone', maskPhone(e.target.value))}
                                    placeholder="(00) 00000-0000"
                                    inputMode="numeric"
                                />
                            </Field>
                        </div>
                    </section>

                    <hr className="border-gray-100 dark:border-zinc-800" />

                    {/* Endereço */}
                    <section>
                        <SectionTitle>Endereço</SectionTitle>
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                            <Field label="Logradouro" className="sm:col-span-2 lg:col-span-2">
                                <input className={inputCls} value={form.address} onChange={(e) => set('address', e.target.value)} />
                            </Field>
                            <Field label="Bairro">
                                <input className={inputCls} value={form.neighborhood} onChange={(e) => set('neighborhood', e.target.value)} />
                            </Field>
                            <Field label="CEP">
                                <input
                                    className={cn(inputCls, 'font-mono')}
                                    value={form.zip_code}
                                    onChange={(e) => set('zip_code', maskCep(e.target.value))}
                                    placeholder="00000-000"
                                    inputMode="numeric"
                                />
                            </Field>
                            <Field label="Cidade">
                                <input className={inputCls} value={form.city} onChange={(e) => set('city', e.target.value)} />
                            </Field>
                            <Field label="UF">
                                <input
                                    className={cn(inputCls, 'uppercase')}
                                    maxLength={2}
                                    value={form.state}
                                    onChange={(e) => set('state', e.target.value.toUpperCase())}
                                    placeholder="SP"
                                />
                            </Field>
                        </div>
                    </section>

                    <hr className="border-gray-100 dark:border-zinc-800" />

                    {/* Vinculação */}
                    <section>
                        <SectionTitle>Vinculação</SectionTitle>
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                            <Field label="Franqueadora / Grupo" className="sm:col-span-2 lg:col-span-3">
                                <div ref={parentRef} className="relative">
                                    <div
                                        className={cn(inputCls, 'flex cursor-pointer items-center justify-between gap-2')}
                                        onClick={() => { setParentOpen((v) => !v); setParentSearch(''); }}
                                    >
                                        <span className={form.parent_id ? 'text-gray-800 dark:text-zinc-200' : 'text-gray-400 dark:text-zinc-600'}>
                                            {form.parent_id ? selectedParentLabel() : '— Nenhuma (independente) —'}
                                        </span>
                                        <div className="flex shrink-0 items-center gap-1.5">
                                            {form.parent_id && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => { e.stopPropagation(); set('parent_id', ''); }}
                                                    className="rounded p-0.5 text-gray-400 hover:text-red-500"
                                                >
                                                    <PiXBold className="size-3" />
                                                </button>
                                            )}
                                            <PiCaretDownBold className={cn('size-3 text-gray-400 transition-transform', parentOpen && 'rotate-180')} />
                                        </div>
                                    </div>

                                    {parentOpen && (
                                        <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl dark:border-zinc-700 dark:bg-zinc-900">
                                            <div className="flex items-center gap-2 border-b border-gray-100 px-3 py-2 dark:border-zinc-800">
                                                <PiMagnifyingGlassBold className="size-3.5 shrink-0 text-gray-400" />
                                                <input
                                                    autoFocus
                                                    className="flex-1 bg-transparent text-sm text-gray-800 outline-none placeholder:text-gray-400 dark:text-zinc-200"
                                                    placeholder="Buscar franqueadora…"
                                                    value={parentSearch}
                                                    onChange={(e) => setParentSearch(e.target.value)}
                                                />
                                                {parentFetching && (
                                                    <PiArrowsClockwiseBold className="size-3.5 shrink-0 animate-spin text-gray-400" />
                                                )}
                                            </div>
                                            <div className="max-h-52 overflow-y-auto">
                                                <button
                                                    type="button"
                                                    onClick={() => { set('parent_id', ''); setParentOpen(false); }}
                                                    className="w-full px-4 py-2.5 text-left text-sm italic text-gray-400 transition hover:bg-gray-50 dark:text-zinc-600 dark:hover:bg-zinc-800"
                                                >
                                                    — Nenhuma (independente) —
                                                </button>
                                                {(parentOptions ?? []).length === 0 && !parentFetching && (
                                                    <p className="px-4 py-3 text-sm text-gray-400 dark:text-zinc-600">Nenhum resultado</p>
                                                )}
                                                {(parentOptions ?? []).map((p) => (
                                                    <button
                                                        key={p.id}
                                                        type="button"
                                                        onClick={() => { set('parent_id', String(p.id)); setParentOpen(false); }}
                                                        className={cn(
                                                            'w-full px-4 py-2.5 text-left transition hover:bg-amber-50 dark:hover:bg-amber-500/10',
                                                            String(p.id) === form.parent_id && 'bg-amber-50 dark:bg-amber-500/10',
                                                        )}
                                                    >
                                                        <p className={cn('text-sm font-medium', String(p.id) === form.parent_id ? 'text-amber-700 dark:text-amber-400' : 'text-gray-800 dark:text-zinc-200')}>
                                                            {p.name}
                                                        </p>
                                                        {p.legal_name && p.legal_name !== p.name && (
                                                            <p className="text-[11px] text-gray-400 dark:text-zinc-600">{p.legal_name}</p>
                                                        )}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <p className="text-[10px] text-gray-400 dark:text-zinc-600">
                                    Selecione se este cliente é uma unidade de uma rede/franqueadora.
                                </p>
                            </Field>

                            <Field label="Status">
                                <div className="flex items-center gap-3">
                                    <button
                                        type="button"
                                        onClick={() => set('active', !form.active)}
                                        className={cn(
                                            'relative shrink-0 rounded-full transition-colors duration-200',
                                            form.active ? 'bg-emerald-500' : 'bg-zinc-300 dark:bg-zinc-700',
                                        )}
                                        style={{ width: 44, height: 24 }}
                                    >
                                        <span
                                            className="absolute top-[2px] size-5 rounded-full bg-white shadow-md transition-all duration-200"
                                            style={{ left: form.active ? 22 : 2 }}
                                        />
                                    </button>
                                    <p className="text-sm text-gray-500 dark:text-zinc-400">
                                        {form.active ? 'Ativo' : 'Inativo'}
                                    </p>
                                </div>
                            </Field>
                        </div>
                    </section>

                    <hr className="border-gray-100 dark:border-zinc-800" />

                    {/* Observações */}
                    <section>
                        <SectionTitle>Observações</SectionTitle>
                        <textarea
                            className={cn(inputCls, 'min-h-[80px] resize-y')}
                            value={form.notes}
                            onChange={(e) => set('notes', e.target.value)}
                            placeholder="Notas internas sobre este cliente…"
                        />
                    </section>
                </div>
            )}

            {/* Footer actions */}
            <div className="flex items-center justify-end gap-3">
                <Link
                    to={isEdit ? `/clients/${id}` : '/clients'}
                    className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-bold text-gray-500 transition hover:bg-gray-50 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-800"
                >
                    Cancelar
                </Link>
                <button
                    type="button"
                    onClick={() => save()}
                    disabled={saving || (isEdit && loadingClient)}
                    className="flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-2 text-sm font-black text-white transition hover:bg-amber-600 disabled:opacity-60 dark:bg-[#C9A84C] dark:text-zinc-900 dark:hover:bg-amber-500"
                >
                    {saving ? (
                        <PiArrowsClockwiseBold className="size-4 animate-spin" />
                    ) : isEdit ? (
                        <PiFloppyDiskBold className="size-4" />
                    ) : (
                        <PiPlusBold className="size-4" />
                    )}
                    {saving ? 'Salvando…' : isEdit ? 'Salvar alterações' : 'Criar cliente'}
                </button>
            </div>
        </div>
    );
}
